import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Home, LogOut, User, ChevronDown, Settings } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

export const Header: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [fullName, setFullName] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleNavigateHome = () => {
    navigate('/')
  }

  const isAppView = location.pathname.startsWith('/app/')

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!user) return
    supabase
      .from('user_profiles')
      .select('full_name')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.full_name) setFullName(data.full_name)
      })
  }, [user])

  const displayName = fullName || user?.email?.split('@')[0]

  const handleSignOut = async () => {
    setIsDropdownOpen(false)
    await signOut()
  }

  return (
    <div style={{ 
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      padding: '16px 0'
    }}>
      <div style={{ 
        maxWidth: '1280px', 
        margin: '0 auto', 
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <a 
          href="https://www.solharbor.com" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            textDecoration: 'none',
            cursor: 'pointer'
          }}
        >
          <img 
            src="/logo.png" 
            alt="Solharbor Logo" 
            style={{ height: '40px', width: '40px', marginRight: '12px' }}
          />
          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#3b82f6',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Solharbor
          </h1>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {isAppView && (
            <button
              onClick={handleNavigateHome}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: '#f3f4f6',
                color: '#374151',
                fontWeight: '500',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                const target = e.target as HTMLButtonElement;
                target.style.background = '#e5e7eb';
                target.style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                const target = e.target as HTMLButtonElement;
                target.style.background = '#f3f4f6';
                target.style.color = '#374151';
              }}
            >
              <Home style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              Portal Home
            </button>
          )}
          
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 12px',
                background: 'white',
                color: '#374151',
                fontWeight: '500',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                const target = e.target as HTMLButtonElement;
                if (!isDropdownOpen) {
                  target.style.background = '#f3f4f6';
                }
              }}
              onMouseLeave={(e) => {
                const target = e.target as HTMLButtonElement;
                if (!isDropdownOpen) {
                  target.style.background = 'white';
                }
              }}
            >
              <div style={{
                height: '24px',
                width: '24px',
                backgroundColor: '#e5e7eb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <User style={{ height: '14px', width: '14px', color: '#6b7280' }} />
              </div>
              <span style={{
                maxWidth: '160px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {displayName}
              </span>
              <ChevronDown style={{ 
                height: '16px', 
                width: '16px', 
                color: '#6b7280',
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} />
            </button>

            {isDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '4px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb',
                minWidth: '200px',
                zIndex: 50,
                overflow: 'hidden'
              }}>
                <div style={{ padding: '8px' }}>
                  <div style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid #f3f4f6',
                    marginBottom: '4px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      marginBottom: '2px'
                    }}>
                      {displayName}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      {user?.email}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => { setIsDropdownOpen(false); navigate('/profile'); }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: 'transparent',
                      color: '#374151',
                      fontSize: '14px',
                      fontFamily: 'Lexend Deca, sans-serif',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Settings style={{ height: '16px', width: '16px', color: '#6b7280' }} />
                    Profile
                  </button>
                  <button
                    onClick={handleSignOut}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: 'transparent',
                      color: '#374151',
                      fontSize: '14px',
                      fontFamily: 'Lexend Deca, sans-serif',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <LogOut style={{ height: '16px', width: '16px', color: '#6b7280' }} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}